import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useDropzone } from "react-dropzone";
import html2canvas from "html2canvas";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, FileText, Mail, Send, Loader2, Plus, Trash2,
  Image, FileImage, Download, Eye, Edit, CheckCircle, Clock,
  AlertTriangle, Camera, Upload, Star, GripVertical, X, Home,
  FileSignature, PenTool, RefreshCw, Globe
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { API } from "@/App";

// Document types with labels
const DOCUMENT_TYPES = [
  { value: "valuation_letter", label: "Valuation Letter", icon: FileText },
  { value: "instruction_form", label: "Instruction Form (Contract)", icon: FileText },
  { value: "memorandum_of_sale", label: "Memorandum of Sale", icon: FileText },
  { value: "invoice", label: "Commission Invoice", icon: FileText },
  { value: "exchange_letter", label: "Exchange Letter", icon: Mail },
  { value: "completion_letter", label: "Completion Letter", icon: CheckCircle },
  { value: "price_reduction_letter", label: "Price Reduction Letter", icon: AlertTriangle },
  { value: "withdrawal_letter", label: "Withdrawal Letter", icon: X },
  { value: "cancellation_letter", label: "Cancellation Letter", icon: X },
];

const STATUS_BADGES = {
  draft: { label: "Draft", className: "bg-slate-100 text-slate-700" },
  pending_review: { label: "Pending Review", className: "bg-amber-100 text-amber-700" },
  amendments_requested: { label: "Amendments Requested", className: "bg-orange-100 text-orange-700" },
  approved: { label: "Approved", className: "bg-green-100 text-green-700" },
  sent: { label: "Sent", className: "bg-blue-100 text-blue-700" },
  signed: { label: "Signed", className: "bg-emerald-100 text-emerald-700" },
  completed: { label: "Completed", className: "bg-green-100 text-green-700" },
};

export const CaseDocumentsPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [floorplans, setFloorplans] = useState([]);
  const [epcDocuments, setEpcDocuments] = useState([]);
  const [idDocuments, setIdDocuments] = useState([]);
  const [particulars, setParticulars] = useState(null);
  
  // Property details editing
  const [description, setDescription] = useState("");
  const [savingDescription, setSavingDescription] = useState(false);

  // Modal states
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showInstructionModal, setShowInstructionModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedDocType, setSelectedDocType] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [instructionContract, setInstructionContract] = useState(null);
  const [generatingContract, setGeneratingContract] = useState(false);
  
  // DocuSign state
  const [docusignStatus, setDocusignStatus] = useState(null);
  const [docusignEnvelope, setDocusignEnvelope] = useState(null);
  const [sendingDocusign, setSendingDocusign] = useState(false);
  const [showDocusignModal, setShowDocusignModal] = useState(false);

  // Send form
  const [sendForm, setSendForm] = useState({
    recipient_email: "",
    recipient_name: "",
    subject: "",
    message: ""
  });

  // Instruction form state
  const [instructionForm, setInstructionForm] = useState({
    vendor_name: "",
    vendor_email: "",
    vendor_address: "",
    vendor_phone: "",
    asking_price: 0,
    commission_percent: 1.5,
    agency_type: "sole_agency",
    agency_period_weeks: 12,
    minimum_term_weeks: 8,
    notice_period_weeks: 2,
    tie_in_clause: true,
    marketing_contribution: 0,
    special_conditions: ""
  });

  useEffect(() => {
    fetchData();
  }, [caseId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [caseRes, docsRes, photosRes, particularsRes, instructionRes, epcRes, idsRes] = await Promise.all([
        axios.get(`${API}/sales/cases/${caseId}`, { headers }),
        axios.get(`${API}/documents/case/${caseId}`, { headers }),
        axios.get(`${API}/documents/photos/case/${caseId}`, { headers }),
        axios.get(`${API}/documents/particulars/case/${caseId}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/esignature/sales-instruction/case/${caseId}`, { headers }).catch(() => ({ data: { document: null } })),
        axios.get(`${API}/documents/epc/${caseId}`, { headers }).catch(() => ({ data: { document: null } })),
        axios.get(`${API}/documents/ids/${caseId}`, { headers }).catch(() => ({ data: { documents: [] } }))
      ]);

      const caseInfo = caseRes.data.case;
      setCaseData(caseInfo);
      
      // Regular documents (exclude EPC and IDs which have their own endpoints)
      const allDocs = docsRes.data.documents || [];
      setDocuments(allDocs.filter(d => !d.type?.includes('epc') && !d.type?.includes('id_')));
      
      // EPC from dedicated endpoint
      if (epcRes.data.document) {
        setEpcDocuments([epcRes.data.document]);
      } else {
        setEpcDocuments([]);
      }
      
      // IDs from dedicated endpoint
      setIdDocuments(idsRes.data.documents || []);
      
      setPhotos(photosRes.data.photos || []);
      setFloorplans(photosRes.data.floorplans || []);
      setParticulars(particularsRes.data);
      setInstructionContract(instructionRes.data.document);

      // Fetch DocuSign status and envelopes
      try {
        const [dsStatusRes, dsEnvelopesRes] = await Promise.all([
          axios.get(`${API}/docusign/status`, { headers }).catch(() => ({ data: { status: 'not_configured' } })),
          axios.get(`${API}/docusign/envelopes?case_id=${caseId}`, { headers }).catch(() => ({ data: { envelopes: [] } }))
        ]);
        setDocusignStatus(dsStatusRes.data);
        // Get the latest envelope for this case
        const envelopes = dsEnvelopesRes.data.envelopes || [];
        if (envelopes.length > 0) {
          setDocusignEnvelope(envelopes[0]);
        }
      } catch (err) {
        console.log("DocuSign not configured or unavailable");
      }

      // Pre-fill instruction form from case data
      if (caseInfo) {
        setInstructionForm(prev => ({
          ...prev,
          asking_price: caseInfo.asking_price || 0,
          commission_percent: caseInfo.commission_percent || 1.5
        }));
        // Load description
        setDescription(caseInfo.description || "");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  // Save property description
  const handleSaveDescription = async () => {
    setSavingDescription(true);
    try {
      await axios.put(`${API}/sales/cases/${caseId}`, 
        { 
          description,
          summary: caseData?.summary || ""
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Details saved!");
    } catch (error) {
      toast.error("Failed to save details");
    } finally {
      setSavingDescription(false);
    }
  };

  // Preview contract PDF with authentication - BLANK TEMPLATE
  const handlePreviewContract = async () => {
    setGeneratingContract(true);
    try {
      const response = await axios.get(
        `${API}/esignature/sales-contract/blank`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create blob URL and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create a download link and click it
      const link = document.createElement('a');
      link.href = url;
      link.download = `Goodacres_Sales_Agency_Contract.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Also try to open in new tab
      window.open(url, '_blank');
      
      // Clean up after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      toast.success("Blank contract template downloaded!");
    } catch (error) {
      console.error("Error generating contract:", error);
      toast.error(error.response?.data?.detail || "Failed to generate contract");
    } finally {
      setGeneratingContract(false);
    }
  };

  const handleGenerateDocument = async () => {
    if (!selectedDocType) {
      toast.error("Please select a document type");
      return;
    }

    setGenerating(true);
    try {
      // Use different endpoint for invoices
      const endpoint = selectedDocType === "invoice" 
        ? `${API}/documents/invoice/generate`
        : `${API}/documents/generate`;
      
      const payload = selectedDocType === "invoice"
        ? { case_id: caseId }
        : { case_id: caseId, document_type: selectedDocType };

      const res = await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Document generated successfully");
      setShowGenerateModal(false);
      setSelectedDocType("");
      fetchData();

      // Open preview
      setSelectedDocument(res.data);
      setShowPreviewModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate document");
    } finally {
      setGenerating(false);
    }
  };

  const handleSendDocument = async () => {
    if (!sendForm.recipient_email) {
      toast.error("Please enter recipient email");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/documents/${selectedDocument.document_id}/send`, sendForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Document sent successfully");
      setShowSendModal(false);
      setSendForm({ recipient_email: "", recipient_name: "", subject: "", message: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send document");
    } finally {
      setSending(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!confirm("Are you sure you want to delete this document?")) return;

    try {
      await axios.delete(`${API}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Document deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  // Handle sending Sales Instruction Form for signature
  const handleSendInstructionForm = async () => {
    if (!instructionForm.vendor_name || !instructionForm.vendor_email) {
      toast.error("Please fill in vendor name and email");
      return;
    }

    setSending(true);
    try {
      const payload = {
        case_id: caseId,
        vendor_name: instructionForm.vendor_name,
        vendor_email: instructionForm.vendor_email,
        vendor_address: instructionForm.vendor_address,
        vendor_phone: instructionForm.vendor_phone,
        property_address: `${caseData?.address_line1 || ""}, ${caseData?.town || ""}, ${caseData?.postcode || ""}`.trim().replace(/^,\s*|,\s*$/g, ""),
        property_postcode: caseData?.postcode || "",
        asking_price: parseFloat(instructionForm.asking_price) || 0,
        commission_percent: parseFloat(instructionForm.commission_percent) || 1.5,
        agency_type: instructionForm.agency_type,
        agency_period_weeks: parseInt(instructionForm.agency_period_weeks) || 12,
        minimum_term_weeks: parseInt(instructionForm.minimum_term_weeks) || 8,
        notice_period_weeks: parseInt(instructionForm.notice_period_weeks) || 2,
        tie_in_clause: instructionForm.tie_in_clause,
        marketing_contribution: parseFloat(instructionForm.marketing_contribution) || 0,
        special_conditions: instructionForm.special_conditions,
        send_copy_to_agency: true
      };

      await axios.post(`${API}/esignature/sales-instruction/send`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Sales Instruction Form sent successfully!");
      setShowInstructionModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send instruction form");
    } finally {
      setSending(false);
    }
  };

  // Handle resending instruction form
  const handleResendInstructionForm = async () => {
    if (!instructionContract?.document_id) return;

    setSending(true);
    try {
      await axios.post(`${API}/esignature/sales-instruction/${instructionContract.document_id}/resend`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Reminder sent successfully!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend");
    } finally {
      setSending(false);
    }
  };

  // Handle sending contract via DocuSign
  const handleSendViaDocuSign = async () => {
    if (!caseData?.seller_email || !caseData?.seller_name) {
      toast.error("Seller email and name are required");
      return;
    }

    setSendingDocusign(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/docusign/send-contract/${caseId}`, {}, { headers });
      
      toast.success(`Contract sent to ${caseData.seller_email} via DocuSign!`);
      setShowDocusignModal(false);
      fetchData();
    } catch (error) {
      console.error("DocuSign error:", error);
      toast.error(error.response?.data?.detail || "Failed to send via DocuSign");
    } finally {
      setSendingDocusign(false);
    }
  };

  // Check DocuSign envelope status
  const handleCheckDocuSignStatus = async () => {
    if (!docusignEnvelope?.envelope_id) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/docusign/envelope/${docusignEnvelope.envelope_id}`, { headers });
      setDocusignEnvelope(prev => ({ ...prev, ...response.data }));
      toast.success(`Status: ${response.data.status}`);
    } catch (error) {
      toast.error("Failed to check status");
    }
  };

  // Download signed document from DocuSign
  const handleDownloadSignedDocument = async () => {
    if (!docusignEnvelope?.envelope_id) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/docusign/envelope/${docusignEnvelope.envelope_id}/download`, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `signed_contract_${caseId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to download - document may not be signed yet");
    }
  };

  // Resend DocuSign envelope
  const handleResendDocuSign = async () => {
    if (!docusignEnvelope?.envelope_id) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/docusign/envelope/${docusignEnvelope.envelope_id}/resend`, {}, { headers });
      toast.success("Reminder sent via DocuSign");
    } catch (error) {
      toast.error("Failed to resend");
    }
  };

  // Pre-fill instruction form with seller data
  const openInstructionModal = async () => {
    // Try to get seller info from case data directly
    let sellerName = caseData?.seller_name || "";
    let sellerEmail = caseData?.seller_email || "";
    let sellerPhone = caseData?.seller_phone || "";
    let price = caseData?.offer_price || caseData?.asking_price || 0;

    // Also try to get from parties
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const partiesRes = await axios.get(`${API}/sales/cases/${caseId}/parties`, { headers });
      const seller = partiesRes.data.parties?.find(p => p.role === "seller");
      
      if (seller) {
        sellerName = seller.name || sellerName;
        sellerEmail = seller.email?.split(';')[0]?.trim() || sellerEmail;  // Take first email if multiple
        sellerPhone = seller.phone || sellerPhone;
      }
    } catch (error) {
      // Use case data as fallback
    }

    setInstructionForm(prev => ({
      ...prev,
      vendor_name: sellerName,
      vendor_email: sellerEmail,
      vendor_phone: sellerPhone,
      asking_price: price,
      commission_percent: caseData?.commission_percent || 1.5
    }));
    
    setShowInstructionModal(true);
  };

  // Handle PDF download (opens in new tab for print)
  const handleDownloadPDF = async (documentId, title) => {
    // Open in new tab - user can use browser print to save as PDF
    window.open(`${API}/documents/${documentId}/pdf`, '_blank');
  };

  // Photo upload dropzone
  const onDrop = useCallback(async (acceptedFiles, isFloorplan = false) => {
    setUploading(true);
    
    for (const file of acceptedFiles) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("case_id", caseId);
      formData.append("is_floorplan", isFloorplan.toString());
      formData.append("is_main_photo", "false");

      try {
        await axios.post(`${API}/documents/photos/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    toast.success(`${acceptedFiles.length} file(s) uploaded`);
    setUploading(false);
    fetchData();
  }, [caseId, token]);

  // Document upload handler for EPC and IDs
  const onDocumentUpload = useCallback(async (acceptedFiles, documentType) => {
    setUploading(true);
    
    for (const file of acceptedFiles) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("case_id", caseId);

      try {
        let endpoint = `${API}/documents/upload`;
        
        if (documentType === "epc_certificate") {
          endpoint = `${API}/documents/epc/upload`;
          formData.append("epc_rating", "C"); // Default rating, can be updated
        } else if (documentType === "id_verification") {
          endpoint = `${API}/documents/id/upload`;
          formData.append("id_type", "passport");
          formData.append("contact_id", caseData?.seller_contact_id || "");
        }

        await axios.post(endpoint, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
      } catch (error) {
        console.error("Upload error:", error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    toast.success(`${acceptedFiles.length} file(s) uploaded`);
    setUploading(false);
    fetchData();
  }, [caseId, token, caseData]);

  const { getRootProps: getPhotoRootProps, getInputProps: getPhotoInputProps, isDragActive: isPhotoDragActive } = useDropzone({
    onDrop: (files) => onDrop(files, false),
    accept: { "image/*": [".jpeg", ".jpg", ".png", ".webp", ".gif"] }
  });

  const { getRootProps: getFloorplanRootProps, getInputProps: getFloorplanInputProps, isDragActive: isFloorplanDragActive } = useDropzone({
    onDrop: (files) => onDrop(files, true),
    accept: { "image/*": [".jpeg", ".jpg", ".png", ".webp", ".gif"], "application/pdf": [".pdf"] }
  });

  const { getRootProps: getEpcRootProps, getInputProps: getEpcInputProps, isDragActive: isEpcDragActive } = useDropzone({
    onDrop: (files) => onDocumentUpload(files, "epc_certificate"),
    accept: { "application/pdf": [".pdf"], "image/*": [".jpeg", ".jpg", ".png"] }
  });

  const { getRootProps: getIdRootProps, getInputProps: getIdInputProps, isDragActive: isIdDragActive } = useDropzone({
    onDrop: (files) => onDocumentUpload(files, "id_verification"),
    accept: { "application/pdf": [".pdf"], "image/*": [".jpeg", ".jpg", ".png"] }
  });

  const handleSetMainPhoto = async (photoId) => {
    try {
      await axios.put(`${API}/documents/photos/${photoId}/main`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Main photo updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to set main photo");
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!confirm("Delete this photo?")) return;

    try {
      await axios.delete(`${API}/documents/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Photo deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  // Save EPC details to case
  const handleSaveEpcDetails = async () => {
    setSavingDescription(true);
    try {
      await axios.put(`${API}/sales/cases/${caseId}`, 
        { 
          epc_rating: caseData?.epc_rating || "",
          epc_potential: caseData?.epc_potential || "",
          epc_certificate_number: caseData?.epc_certificate_number || "",
          epc_expiry_date: caseData?.epc_expiry_date || ""
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("EPC details saved!");
    } catch (error) {
      console.error("Error saving EPC:", error);
      toast.error("Failed to save EPC details");
    } finally {
      setSavingDescription(false);
    }
  };

  // Download EPC graphic as image
  const handleDownloadEpcGraphic = async () => {
    const element = document.getElementById('epc-graphic');
    if (!element) {
      toast.error("EPC graphic not found");
      return;
    }
    
    try {
      // Use html2canvas to capture the EPC graphic
      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: 2
      });
      
      // Create download link
      const link = document.createElement('a');
      link.download = `EPC_${caseData?.postcode || caseId}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success("EPC graphic downloaded!");
    } catch (error) {
      console.error("Error downloading EPC:", error);
      toast.error("Failed to download EPC graphic");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(`/sales/case/${caseId}`)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold">Documents & Photos</h1>
                <p className="text-sm text-muted-foreground">
                  {caseData?.address_line1}, {caseData?.postcode}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate(`/sales/case/${caseId}/marketing`)}>
                <Globe className="w-4 h-4 mr-2" />
                Marketing Details
              </Button>
              <Button variant="outline" onClick={() => navigate(`/sales/case/${caseId}/particulars`)}>
                <Home className="w-4 h-4 mr-2" />
                Sales Particulars
              </Button>
              <Button onClick={() => setShowGenerateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Generate Document
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Property Details Card */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Home className="w-5 h-5" />
              Property Details
            </CardTitle>
            <CardDescription>
              {caseData?.address || caseData?.property_address || "Property Address"} • 
              {caseData?.bedrooms && ` ${caseData.bedrooms} bed`}
              {caseData?.property_type && ` • ${caseData.property_type}`}
              {caseData?.asking_price && ` • £${caseData.asking_price.toLocaleString()}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary - 300 characters max */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Summary</Label>
                <span className={`text-xs ${(caseData?.summary?.length || 0) > 300 ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                  {caseData?.summary?.length || 0}/300 characters
                </span>
              </div>
              <Textarea
                placeholder="Brief summary for portal search results (max 300 characters)..."
                value={caseData?.summary || ""}
                onChange={(e) => {
                  if (e.target.value.length <= 300) {
                    setCaseData({...caseData, summary: e.target.value});
                  }
                }}
                maxLength={300}
                className="min-h-[80px]"
                data-testid="property-summary"
              />
              <p className="text-xs text-muted-foreground mt-1">
                This appears in portal search results. Keep it punchy and highlight key selling points.
              </p>
            </div>

            {/* Full Description */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Full Description (Rightmove)</Label>
                <span className="text-xs text-muted-foreground">
                  {description?.length || 0} characters
                </span>
              </div>
              <Textarea
                placeholder="Full property description for Rightmove, Zoopla & OnTheMarket..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="min-h-[200px]"
                data-testid="property-description"
              />
              <p className="text-xs text-muted-foreground mt-1">
                The complete property description that appears on portal listing pages.
              </p>
            </div>

            <div className="flex justify-end">
              <Button 
                onClick={handleSaveDescription}
                disabled={savingDescription}
                data-testid="save-description-btn"
              >
                {savingDescription ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Save Details
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Sales Instruction Form Card */}
        <Card className="mb-6" data-testid="instruction-form-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  instructionContract?.status === "signed" 
                    ? "bg-green-100" 
                    : instructionContract 
                      ? "bg-amber-100" 
                      : "bg-slate-100"
                }`}>
                  <FileSignature className={`w-6 h-6 ${
                    instructionContract?.status === "signed"
                      ? "text-green-600"
                      : instructionContract
                        ? "text-amber-600"
                        : "text-slate-500"
                  }`} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Sales Instruction Form (Contract)</h3>
                  <p className="text-sm text-muted-foreground">
                    {instructionContract?.status === "signed" 
                      ? `Signed on ${new Date(instructionContract.signed_at).toLocaleDateString()}` 
                      : instructionContract?.status === "viewed"
                        ? "Awaiting signature - Vendor has viewed"
                        : instructionContract?.status === "pending"
                          ? `Sent to ${instructionContract.vendor_email}`
                          : "Send to vendor for e-signature"
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {instructionContract?.status === "signed" ? (
                  <Badge className="bg-green-100 text-green-700">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Signed
                  </Badge>
                ) : instructionContract ? (
                  <>
                    <Badge className="bg-amber-100 text-amber-700">
                      <Clock className="w-3 h-3 mr-1" />
                      {instructionContract.status === "viewed" ? "Viewed" : "Pending"}
                    </Badge>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleResendInstructionForm}
                      disabled={sending}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Resend
                    </Button>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      onClick={handlePreviewContract}
                      disabled={generatingContract}
                      data-testid="preview-contract-btn"
                    >
                      {generatingContract ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Eye className="w-4 h-4 mr-2" />
                      )}
                      Preview Contract
                    </Button>
                    <Button onClick={openInstructionModal} data-testid="send-instruction-btn">
                      <PenTool className="w-4 h-4 mr-2" />
                      Send for Signature
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* DocuSign E-Signature Card */}
        {docusignStatus?.status === "active" && (
          <Card className="mb-6 border-blue-200 bg-gradient-to-r from-blue-50 to-white" data-testid="docusign-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    docusignEnvelope?.status === "completed" 
                      ? "bg-green-100" 
                      : docusignEnvelope 
                        ? "bg-blue-100" 
                        : "bg-slate-100"
                  }`}>
                    <Globe className={`w-6 h-6 ${
                      docusignEnvelope?.status === "completed"
                        ? "text-green-600"
                        : docusignEnvelope
                          ? "text-blue-600"
                          : "text-slate-500"
                    }`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg">DocuSign E-Signature</h3>
                      <Badge variant="outline" className="text-xs text-blue-600 border-blue-200">
                        Professional
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {docusignEnvelope?.status === "completed" 
                        ? `Signed via DocuSign on ${new Date(docusignEnvelope.completed_date_time || docusignEnvelope.created_at).toLocaleDateString()}` 
                        : docusignEnvelope?.status === "sent" || docusignEnvelope?.status === "delivered"
                          ? `Sent to ${docusignEnvelope.signers?.length > 1 ? `${docusignEnvelope.signers.length} signers` : docusignEnvelope.signers?.[0]?.email || caseData?.seller_email} - awaiting signature`
                          : "Send contract via DocuSign for legally binding e-signatures"
                      }
                    </p>
                    {/* Show individual signer progress for multi-signer envelopes */}
                    {docusignEnvelope && docusignEnvelope.signers?.length > 1 && docusignEnvelope.status !== "completed" && (
                      <div className="mt-2 space-y-1">
                        {docusignEnvelope.signers.map((signer, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs">
                            {signer.status === "completed" || signer.status === "signed" ? (
                              <CheckCircle className="w-3 h-3 text-green-600" />
                            ) : (
                              <Clock className="w-3 h-3 text-amber-500" />
                            )}
                            <span className={signer.status === "completed" || signer.status === "signed" ? "text-green-700" : "text-slate-600"}>
                              {signer.name} {signer.status === "completed" || signer.status === "signed" ? "- Signed" : "- Pending"}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {docusignEnvelope?.status === "completed" ? (
                    <>
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Signed
                      </Badge>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleDownloadSignedDocument}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    </>
                  ) : docusignEnvelope ? (
                    <>
                      {/* Show progress badge for multi-signer */}
                      {docusignEnvelope.signers?.length > 1 ? (
                        <Badge className="bg-blue-100 text-blue-700">
                          {docusignEnvelope.signers.filter(s => s.status === "completed" || s.status === "signed").length}/{docusignEnvelope.signers.length} Signed
                        </Badge>
                      ) : (
                        <Badge className="bg-blue-100 text-blue-700">
                          <Clock className="w-3 h-3 mr-1" />
                          {docusignEnvelope.status === "delivered" ? "Delivered" : "Sent"}
                        </Badge>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleCheckDocuSignStatus}
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Check Status
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleResendDocuSign}
                      >
                        <Mail className="w-4 h-4 mr-1" />
                        Resend
                      </Button>
                    </>
                  ) : (
                    <Button 
                      onClick={() => setShowDocusignModal(true)} 
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="send-docusign-btn"
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      Send via DocuSign
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="documents" className="space-y-6">
          <TabsList>
            <TabsTrigger value="documents">
              <FileText className="w-4 h-4 mr-2" />
              Documents ({documents.length})
            </TabsTrigger>
            <TabsTrigger value="photos">
              <Image className="w-4 h-4 mr-2" />
              Photos ({photos.length})
            </TabsTrigger>
            <TabsTrigger value="floorplans">
              <FileImage className="w-4 h-4 mr-2" />
              Floorplans ({floorplans.length})
            </TabsTrigger>
            <TabsTrigger value="epc">
              <FileText className="w-4 h-4 mr-2" />
              EPC ({epcDocuments.length})
            </TabsTrigger>
            <TabsTrigger value="ids">
              <FileText className="w-4 h-4 mr-2" />
              IDs ({idDocuments.length})
            </TabsTrigger>
          </TabsList>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <div className="space-y-4">
              {documents.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-medium text-lg mb-2">No Documents Yet</h3>
                    <p className="text-muted-foreground mb-4">
                      Generate your first document for this property.
                    </p>
                    <Button onClick={() => setShowGenerateModal(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Generate Document
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {documents.map((doc) => {
                    const statusInfo = STATUS_BADGES[doc.status] || STATUS_BADGES.draft;
                    const docType = DOCUMENT_TYPES.find(t => t.value === doc.document_type);
                    const Icon = docType?.icon || FileText;

                    return (
                      <Card key={doc.document_id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <Icon className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium truncate">{doc.title}</h3>
                                <Badge className={statusInfo.className}>
                                  {statusInfo.label}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                Created {new Date(doc.created_at).toLocaleDateString()}
                              </p>
                              {doc.recipient_email && (
                                <p className="text-sm text-muted-foreground">
                                  To: {doc.recipient_email}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedDocument(doc);
                                setShowPreviewModal(true);
                              }}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Preview
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadPDF(doc.document_id, doc.title)}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              PDF
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedDocument(doc);
                                setSendForm({
                                  recipient_email: doc.recipient_email || "",
                                  recipient_name: doc.recipient_name || "",
                                  subject: `${doc.title} - ${caseData?.address_line1}`,
                                  message: ""
                                });
                                setShowSendModal(true);
                              }}
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Send
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto"
                              onClick={() => handleDeleteDocument(doc.document_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos">
            <div className="space-y-4">
              {/* Upload Area */}
              <Card>
                <CardContent className="p-6">
                  <div
                    {...getPhotoRootProps()}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      isPhotoDragActive ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-slate-400"
                    }`}
                  >
                    <input {...getPhotoInputProps()} />
                    {uploading ? (
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
                    ) : (
                      <>
                        <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                        <p className="text-sm text-muted-foreground">
                          {isPhotoDragActive
                            ? "Drop photos here..."
                            : "Drag & drop photos here, or click to select"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Supports: JPEG, PNG, WebP, GIF
                        </p>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Photo Grid */}
              {photos.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {photos.map((photo) => (
                    <div
                      key={photo.photo_id}
                      className="relative group aspect-square rounded-lg overflow-hidden bg-slate-100"
                    >
                      <img
                        src={`${API}${photo.file_url}`}
                        alt={photo.caption || "Property photo"}
                        className="w-full h-full object-cover"
                      />
                      {photo.is_main_photo && (
                        <div className="absolute top-2 left-2">
                          <Badge className="bg-amber-500 text-white">
                            <Star className="w-3 h-3 mr-1" />
                            Main
                          </Badge>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        {!photo.is_main_photo && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleSetMainPhoto(photo.photo_id)}
                          >
                            <Star className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeletePhoto(photo.photo_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {photos.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Camera className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-medium text-lg mb-2">No Photos Yet</h3>
                    <p className="text-muted-foreground">
                      Upload photos of the property above.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Floorplans Tab */}
          <TabsContent value="floorplans">
            <div className="space-y-4">
              {/* Upload Area */}
              <Card>
                <CardContent className="p-6">
                  <div
                    {...getFloorplanRootProps()}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      isFloorplanDragActive ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-slate-400"
                    }`}
                  >
                    <input {...getFloorplanInputProps()} />
                    {uploading ? (
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
                    ) : (
                      <>
                        <FileImage className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                        <p className="text-sm text-muted-foreground">
                          {isFloorplanDragActive
                            ? "Drop floorplans here..."
                            : "Drag & drop floorplans here, or click to select"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Supports: JPEG, PNG, WebP, PDF
                        </p>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Floorplan Grid */}
              {floorplans.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {floorplans.map((fp) => (
                    <div
                      key={fp.photo_id}
                      className="relative group aspect-[4/3] rounded-lg overflow-hidden bg-slate-100 border"
                    >
                      <img
                        src={`${API}${fp.file_url}`}
                        alt={fp.caption || "Floorplan"}
                        className="w-full h-full object-contain p-2"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeletePhoto(fp.photo_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {floorplans.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <FileImage className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-medium text-lg mb-2">No Floorplans Yet</h3>
                    <p className="text-muted-foreground">
                      Upload floorplans of the property above.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* EPC Tab */}
          <TabsContent value="epc">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* EPC Details Form */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">EPC Details</CardTitle>
                  <CardDescription>Enter EPC certificate details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Current Rating</Label>
                      <Select 
                        value={caseData?.epc_rating || ""} 
                        onValueChange={(v) => setCaseData({...caseData, epc_rating: v})}
                      >
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map(r => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Potential Rating</Label>
                      <Select 
                        value={caseData?.epc_potential || ""} 
                        onValueChange={(v) => setCaseData({...caseData, epc_potential: v})}
                      >
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map(r => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Certificate Number</Label>
                    <Input 
                      placeholder="e.g. 0000-0000-0000-0000-0000"
                      value={caseData?.epc_certificate_number || ""}
                      onChange={(e) => setCaseData({...caseData, epc_certificate_number: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Valid Until</Label>
                    <Input 
                      type="date"
                      value={caseData?.epc_expiry_date || ""}
                      onChange={(e) => setCaseData({...caseData, epc_expiry_date: e.target.value})}
                    />
                  </div>
                  <Button onClick={handleSaveEpcDetails} disabled={savingDescription}>
                    {savingDescription ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Save EPC Details
                  </Button>
                </CardContent>
              </Card>

              {/* EPC Graphic Preview */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">EPC Graphic</CardTitle>
                  <CardDescription>Preview for portal listings</CardDescription>
                </CardHeader>
                <CardContent>
                  {caseData?.epc_rating ? (
                    <div className="border rounded-lg overflow-hidden" id="epc-graphic">
                      {/* EPC Header */}
                      <div className="bg-slate-800 text-white p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
                            <span className="text-slate-800 font-bold text-sm">EPC</span>
                          </div>
                          <span className="text-lg font-semibold">Energy Performance Certificate</span>
                        </div>
                        <p className="text-sm text-slate-300">{caseData?.address_line1 || caseData?.address || "Property Address"}, {caseData?.postcode}</p>
                      </div>
                      
                      {/* Energy Rating Scale */}
                      <div className="p-4 bg-white">
                        <div className="flex gap-4">
                          {/* Rating Bars */}
                          <div className="flex-1 space-y-1">
                            {[
                              { rating: 'A', range: '92+', color: '#00845a', width: '35%' },
                              { rating: 'B', range: '81-91', color: '#2ba84a', width: '45%' },
                              { rating: 'C', range: '69-80', color: '#8dce46', width: '55%' },
                              { rating: 'D', range: '55-68', color: '#ffd500', width: '65%' },
                              { rating: 'E', range: '39-54', color: '#fcaa65', width: '75%' },
                              { rating: 'F', range: '21-38', color: '#ef8023', width: '85%' },
                              { rating: 'G', range: '1-20', color: '#e9153b', width: '100%' },
                            ].map(({ rating, range, color, width }) => (
                              <div key={rating} className="flex items-center gap-2">
                                <div 
                                  className={`h-6 flex items-center justify-between px-2 text-white text-sm font-bold ${caseData?.epc_rating === rating ? 'ring-2 ring-slate-800' : ''}`}
                                  style={{ backgroundColor: color, width }}
                                >
                                  <span>{rating}</span>
                                  <span className="text-xs">{range}</span>
                                </div>
                                {caseData?.epc_rating === rating && (
                                  <div className="bg-slate-800 text-white px-2 py-1 text-xs font-bold rounded">
                                    CURRENT
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Certificate Details */}
                        <div className="mt-4 pt-4 border-t text-sm text-slate-600 space-y-1">
                          {caseData?.epc_certificate_number && (
                            <p><span className="font-medium">Certificate number:</span> {caseData.epc_certificate_number}</p>
                          )}
                          {caseData?.epc_expiry_date && (
                            <p><span className="font-medium">Valid until:</span> {new Date(caseData.epc_expiry_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>Enter EPC details to generate graphic</p>
                    </div>
                  )}
                  
                  {caseData?.epc_rating && (
                    <Button 
                      onClick={handleDownloadEpcGraphic} 
                      className="mt-4 w-full"
                      variant="outline"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download EPC Graphic
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Upload original EPC certificate */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-lg">Original EPC Certificate</CardTitle>
                <CardDescription>Upload the official EPC document</CardDescription>
              </CardHeader>
              <CardContent>
                <div
                  {...getEpcRootProps()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    isEpcDragActive ? "border-green-500 bg-green-50" : "border-slate-300 hover:border-slate-400"
                  }`}
                >
                  <input {...getEpcInputProps()} />
                  {uploading ? (
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-green-600" />
                  ) : (
                    <>
                      <FileText className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-muted-foreground">
                        {isEpcDragActive
                          ? "Drop EPC certificate here..."
                          : "Drag & drop EPC certificate here, or click to select"}
                      </p>
                    </>
                  )}
                </div>
                
                {/* Uploaded EPC documents */}
                {epcDocuments.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {epcDocuments.map((doc) => (
                      <div key={doc.document_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-green-600" />
                          <span className="font-medium">{doc.title || "EPC Certificate"}</span>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => window.open(`${API}/documents/${doc.document_id}/pdf`, '_blank')}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteDocument(doc.document_id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* IDs Tab */}
          <TabsContent value="ids">
            <div className="space-y-4">
              {/* Upload Area */}
              <Card>
                <CardContent className="p-6">
                  <div
                    {...getIdRootProps()}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      isIdDragActive ? "border-purple-500 bg-purple-50" : "border-slate-300 hover:border-slate-400"
                    }`}
                  >
                    <input {...getIdInputProps()} />
                    {uploading ? (
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-600" />
                    ) : (
                      <>
                        <FileText className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                        <p className="text-sm text-muted-foreground">
                          {isIdDragActive
                            ? "Drop ID documents here..."
                            : "Drag & drop ID documents here, or click to select"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Passport, Driving Licence, Utility Bills - PDF, JPEG, PNG
                        </p>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* ID Documents */}
              {idDocuments.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {idDocuments.map((doc) => (
                    <Card key={doc.document_id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-purple-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium truncate">{doc.title || "ID Document"}</h3>
                            <p className="text-sm text-muted-foreground">
                              Uploaded {new Date(doc.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                          <Button size="sm" variant="outline" onClick={() => window.open(`${API}/documents/${doc.document_id}/pdf`, '_blank')}>
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto"
                            onClick={() => handleDeleteDocument(doc.document_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-medium text-lg mb-2">No ID Documents Yet</h3>
                    <p className="text-muted-foreground">
                      Upload vendor identification documents above.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Generate Document Modal */}
      <Dialog open={showGenerateModal} onOpenChange={setShowGenerateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Document</DialogTitle>
            <DialogDescription>
              Select a document type to generate for this property.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              {DOCUMENT_TYPES.map((docType) => {
                const Icon = docType.icon;
                return (
                  <button
                    key={docType.value}
                    onClick={() => setSelectedDocType(docType.value)}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      selectedDocType === docType.value
                        ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <Icon className={`w-5 h-5 mb-2 ${
                      selectedDocType === docType.value ? "text-blue-600" : "text-slate-500"
                    }`} />
                    <p className="font-medium text-sm">{docType.label}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerateDocument} disabled={generating || !selectedDocType}>
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Generate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedDocument?.title}</DialogTitle>
          </DialogHeader>

          <div className="border rounded-lg p-6 bg-white">
            {/* Letterhead */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-slate-700 rounded flex items-center justify-center text-white text-2xl font-bold">
                  G
                </div>
                <div>
                  <h2 className="font-bold text-lg">Goodacres Residential</h2>
                  <p className="text-sm text-muted-foreground">High Street, Kempston</p>
                </div>
              </div>
            </div>

            {/* Document Content */}
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: selectedDocument?.content_html || "" }}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setShowPreviewModal(false);
              setSendForm({
                recipient_email: selectedDocument?.recipient_email || "",
                recipient_name: selectedDocument?.recipient_name || "",
                subject: `${selectedDocument?.title} - ${caseData?.address_line1}`,
                message: ""
              });
              setShowSendModal(true);
            }}>
              <Send className="w-4 h-4 mr-2" />
              Send Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Modal */}
      <Dialog open={showSendModal} onOpenChange={setShowSendModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Document</DialogTitle>
            <DialogDescription>
              Send &ldquo;{selectedDocument?.title}&rdquo; via email.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Recipient Email *</Label>
              <Input
                type="email"
                value={sendForm.recipient_email}
                onChange={(e) => setSendForm({ ...sendForm, recipient_email: e.target.value })}
                placeholder="email@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label>Recipient Name</Label>
              <Input
                value={sendForm.recipient_name}
                onChange={(e) => setSendForm({ ...sendForm, recipient_name: e.target.value })}
                placeholder="John Smith"
              />
            </div>

            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={sendForm.subject}
                onChange={(e) => setSendForm({ ...sendForm, subject: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Message (optional)</Label>
              <Textarea
                value={sendForm.message}
                onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })}
                placeholder="Add a personal message..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendDocument} disabled={sending}>
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sales Instruction Form Modal */}
      <Dialog open={showInstructionModal} onOpenChange={setShowInstructionModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Send Sales Instruction Form</DialogTitle>
            <DialogDescription>
              Send the sales contract to the vendor for electronic signature.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Vendor Details Section */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-slate-500 uppercase tracking-wide">Vendor Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vendor Name *</Label>
                  <Input
                    value={instructionForm.vendor_name}
                    onChange={(e) => setInstructionForm({ ...instructionForm, vendor_name: e.target.value })}
                    placeholder="Mr & Mrs Smith"
                    data-testid="instruction-vendor-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vendor Email *</Label>
                  <Input
                    type="email"
                    value={instructionForm.vendor_email}
                    onChange={(e) => setInstructionForm({ ...instructionForm, vendor_email: e.target.value })}
                    placeholder="vendor@email.com"
                    data-testid="instruction-vendor-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vendor Phone</Label>
                  <Input
                    value={instructionForm.vendor_phone}
                    onChange={(e) => setInstructionForm({ ...instructionForm, vendor_phone: e.target.value })}
                    placeholder="07xxx xxx xxx"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Vendor Address</Label>
                  <Input
                    value={instructionForm.vendor_address}
                    onChange={(e) => setInstructionForm({ ...instructionForm, vendor_address: e.target.value })}
                    placeholder="123 High Street, Bedford, MK40 1AA"
                  />
                </div>
              </div>
            </div>

            {/* Sale Terms Section */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-slate-500 uppercase tracking-wide">Sale Terms</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Asking Price (£)</Label>
                  <Input
                    type="number"
                    value={instructionForm.asking_price}
                    onChange={(e) => setInstructionForm({ ...instructionForm, asking_price: e.target.value })}
                    placeholder="350000"
                    data-testid="instruction-asking-price"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Commission (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={instructionForm.commission_percent}
                    onChange={(e) => setInstructionForm({ ...instructionForm, commission_percent: e.target.value })}
                    placeholder="1.5"
                  />
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>Commission Amount:</strong> £{(instructionForm.asking_price * instructionForm.commission_percent / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} + VAT = 
                  £{(instructionForm.asking_price * instructionForm.commission_percent / 100 * 1.2).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} inc. VAT
                </p>
              </div>
            </div>

            {/* Agency Terms Section */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-slate-500 uppercase tracking-wide">Agency Terms</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Agency Type</Label>
                  <Select 
                    value={instructionForm.agency_type} 
                    onValueChange={(v) => setInstructionForm({ ...instructionForm, agency_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sole_agency">Sole Agency</SelectItem>
                      <SelectItem value="joint_sole_agency">Joint Sole Agency</SelectItem>
                      <SelectItem value="multi_agency">Multi Agency</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Agency Period (weeks)</Label>
                  <Input
                    type="number"
                    value={instructionForm.agency_period_weeks}
                    onChange={(e) => setInstructionForm({ ...instructionForm, agency_period_weeks: e.target.value })}
                    placeholder="12"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Minimum Term (weeks)</Label>
                  <Input
                    type="number"
                    value={instructionForm.minimum_term_weeks}
                    onChange={(e) => setInstructionForm({ ...instructionForm, minimum_term_weeks: e.target.value })}
                    placeholder="8"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notice Period (weeks)</Label>
                  <Input
                    type="number"
                    value={instructionForm.notice_period_weeks}
                    onChange={(e) => setInstructionForm({ ...instructionForm, notice_period_weeks: e.target.value })}
                    placeholder="2"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="tie_in_clause"
                  checked={instructionForm.tie_in_clause}
                  onCheckedChange={(checked) => setInstructionForm({ ...instructionForm, tie_in_clause: checked })}
                />
                <Label htmlFor="tie_in_clause" className="cursor-pointer">
                  Include tie-in clause (standard terms)
                </Label>
              </div>
            </div>

            {/* Additional Section */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-slate-500 uppercase tracking-wide">Additional</h4>
              <div className="space-y-2">
                <Label>Marketing Contribution (£)</Label>
                <Input
                  type="number"
                  value={instructionForm.marketing_contribution}
                  onChange={(e) => setInstructionForm({ ...instructionForm, marketing_contribution: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Special Conditions</Label>
                <Textarea
                  value={instructionForm.special_conditions}
                  onChange={(e) => setInstructionForm({ ...instructionForm, special_conditions: e.target.value })}
                  placeholder="Any special conditions or notes..."
                  rows={3}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInstructionModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendInstructionForm} 
              disabled={sending || !instructionForm.vendor_name || !instructionForm.vendor_email}
              data-testid="send-instruction-submit"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send for Signature
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DocuSign Send Contract Modal */}
      <Dialog open={showDocusignModal} onOpenChange={setShowDocusignModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" />
              Send Contract via DocuSign
            </DialogTitle>
            <DialogDescription>
              Send the sales agency contract to the seller for electronic signature using DocuSign.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">What will happen:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Seller receives email from DocuSign</li>
                <li>• They can sign from any device</li>
                <li>• You will be notified when signed</li>
                <li>• Legally binding e-signature</li>
              </ul>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-sm text-muted-foreground">Property</Label>
                <p className="font-medium">{caseData?.address_line1}, {caseData?.postcode}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Seller Name</Label>
                <p className="font-medium">{caseData?.seller_name || "Not set"}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Seller Email</Label>
                <p className="font-medium">{caseData?.seller_email || "Not set"}</p>
              </div>
            </div>

            {(!caseData?.seller_email || !caseData?.seller_name) && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-sm">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                Please ensure seller name and email are set on the case before sending.
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocusignModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendViaDocuSign}
              disabled={sendingDocusign || !caseData?.seller_email || !caseData?.seller_name}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-docusign-send"
            >
              {sendingDocusign ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send via DocuSign
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CaseDocumentsPage;
